declare global { interface Window { __ASYMMETRIC_ACCOUNT_ID__?: string; } }
export function displayPreferenceKey(accountId?: string) { return `asymmetric-display-preferences:${typeof window !== "undefined" ? accountId ?? window.__ASYMMETRIC_ACCOUNT_ID__ ?? "local-test-owner" : "local-test-owner"}`; }
export const workspaceViews = ["agent", "sources", "systems", "timeline", "simulations", "media", "portfolio", "world-model"] as const;
export type WorkspaceView = typeof workspaceViews[number];
export type DisplayPreferences = {
  appearance: "light" | "dark" | "system";
  textSize: "default" | "large";
  reduceMotion: boolean;
  defaultScreen: WorkspaceView;
  timeZone: string;
  dateFormat: "written" | "numeric" | "iso";
};
export const defaultDisplayPreferences: DisplayPreferences = {
  appearance: "system", textSize: "default", reduceMotion: false,
  defaultScreen: "agent", timeZone: "America/New_York", dateFormat: "written",
};
export function normalizeDisplayPreferences(input: Partial<DisplayPreferences> | null | undefined): DisplayPreferences {
  const value = input && typeof input === "object" ? input : {};
  let timeZone = value.timeZone ?? defaultDisplayPreferences.timeZone;
  try { new Intl.DateTimeFormat("en-US", { timeZone }).format(); } catch { timeZone = defaultDisplayPreferences.timeZone; }
  return {
    appearance: ["light", "dark", "system"].includes(value.appearance ?? "") ? value.appearance! : "system",
    textSize: value.textSize === "large" ? "large" : "default",
    reduceMotion: value.reduceMotion === true,
    defaultScreen: workspaceViews.includes(value.defaultScreen as WorkspaceView) ? value.defaultScreen! : "agent",
    timeZone,
    dateFormat: value.dateFormat === "numeric" || value.dateFormat === "iso" ? value.dateFormat : "written",
  };
}
export function readDisplayPreferences(accountId?: string): DisplayPreferences {
  if (typeof window === "undefined") return defaultDisplayPreferences;
  try {
    const saved = localStorage.getItem(displayPreferenceKey(accountId));
    if (saved) return normalizeDisplayPreferences(JSON.parse(saved));
    const legacy = localStorage.getItem("asymmetric-theme");
    return normalizeDisplayPreferences({ appearance: legacy === "light" || legacy === "dark" ? legacy : "system" });
  } catch { return defaultDisplayPreferences; }
}
export function applyDisplayPreferences(preferences: DisplayPreferences) {
  const theme = preferences.appearance === "system"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    : preferences.appearance;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.documentElement.dataset.textSize = preferences.textSize;
  document.documentElement.dataset.reduceMotion = String(preferences.reduceMotion);
}
export function formatWorkspaceDate(value: string | number | Date, options: Intl.DateTimeFormatOptions = {}) {
  const date = new Date(value);
  if (!Number.isFinite(date.valueOf())) return "Date unavailable";
  const preferences = readDisplayPreferences();
  if (preferences.dateFormat === "iso") {
    return new Intl.DateTimeFormat("sv-SE", { ...options, year: "numeric", month: "2-digit", day: "2-digit", timeZone: preferences.timeZone }).format(date);
  }
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    ...options, month: preferences.dateFormat === "numeric" ? "2-digit" : options.month ?? "short", timeZone: preferences.timeZone,
  }).format(date);
}
